<template>
  <v-dialog
    :content-class="'add-api-key-popup-content'"
    :max-width='650'
    :value="visiblePopup"
    @click:outside='onCloseClickButton'
    @keydown.esc="onCloseClickButton"
  >
    <div class='add-api-key-popup' :class='{ "display-created-api-key": displayCreatedApiKey }'>
      <div class='add-api-key-popup__create-api-key' v-if='!displayCreatedApiKey'>
        <div class='add-api-key-popup__create-api-key__action'>
        <span class='close_btn' @click='onCloseClickButton'>
          <span class="material-icons-outlined">
            close
          </span>
        </span>
        </div>
        <div class='add-api-key-popup__create-api-key__header'>
        <span class='header-label'>
          {{ $t('add.api.key.popup.title') }}
        </span>
        </div>
        <div class='add-api-key-popup__create-api-key__form'>
          <CustomSelect
            class-name="type-select"
            v-model='expiration'
            :options='expirationItems'
            :title="$t('add.api.key.popup.expiration.label').toString()"
            :placeholder="$t('add.api.key.popup.expiration.placeholder').toString()"
            size="small"
          />
          <div class='settings-field'>
            <span class='label'>{{ $t('add.api.key.popup.description.label') }}</span>
            <text-field class='text-field'
                        :placeholder="$t('add.api.key.popup.description.placeholder').toString()"
                        v-model='description'
            />
          </div>
        </div>
        <div class='add-api-key-popup__create-api-key__footer'>
          <div class="buttons">
            <v-btn small depressed class="btn-secondary cancel-button" @click="onCloseClickButton"> {{ $t('confirm.cancel') }} </v-btn>
            <v-btn class="btn-primary save-button" :disabled="saveButtonDisabled" @click="onSaveClick" :loading='loading'>
              {{ $t('save') }}
            </v-btn>
          </div>
        </div>
      </div>
      <div class='add-api-key-popup__display-result' v-else>
        <div class='add-api-key-popup__display-result__header'>
          <span class='header-label'>
            {{ $t('add.api.key.popup.created.api.key.label') }}
          </span>
        </div>
        <div class='add-api-key-popup__display-result__comment'>
           <span class='header-comment'>
            {{ $t('add.api.key.popup.created.api.key.comment') }}
          </span>
        </div>
        <div class='add-api-key-popup__display-result__body'>
          <div class='add-api-key-popup__display-result__body__user-pool-id'>
            <div class='add-api-key-popup__display-result__body__user-pool-id__label'>
              {{ $t('user.pool.id.label') }}
            </div>
            <div id='user-pool-id' class='add-api-key-popup__display-result__body__user-pool-id__value'>
              {{ userPoolId }}
            </div>
            <div class='add-api-key-popup__display-result__body__user-pool-id__copy'>
              <v-btn icon @click="copyValue('user-pool-id')">
                <v-icon>content_copy</v-icon>
              </v-btn>
            </div>
          </div>
          <div class='add-api-key-popup__display-result__body__api-key'>
            <div class='add-api-key-popup__display-result__body__api-key__label'>
              {{ $t('api.key.label') }}
            </div>
            <div id='api-key' class='add-api-key-popup__display-result__body__api-key__value'>
              {{ createdApiKey.apiKey }}
            </div>
            <div class='add-api-key-popup__display-result__body__api-key__copy'>
              <v-btn icon @click="copyValue('api-key')">
                <v-icon>content_copy</v-icon>
              </v-btn>
            </div>
          </div>
        </div>
        <div class='add-api-key-popup__display-result__footer'>
          <div class="buttons">
            <v-btn small depressed class="ok-button btn-primary" @click="onCloseClickButton"> {{ $t('confirm.ok') }} </v-btn>
          </div>
        </div>
      </div>
    </div>
  </v-dialog>
</template>

<script>
import CustomSelect from '@/components/common/Select.vue';
import TextField from '@/components/common/TextField.vue';
import { EXPIRATION_DAYS_AMOUNT } from '@/constants/apiKey';
import { mapGetters } from 'vuex';
import { CLEAR_CREATED_API_KEY } from '@/store/apiKeys/actionTypes';
import disableOuterScrollOnPopupVisible from '@/mixins/disableOuterScrollOnPopupVisible';

export default {
  name: 'AddApiKeyPopup',
  mixins: [disableOuterScrollOnPopupVisible],
  components: {
    CustomSelect,
    TextField,
  },
  props: {
    visiblePopup: {
      type: Boolean,
      required: true,
    },
  },
  data() {
    return {
      description: '',
      expiration: `${EXPIRATION_DAYS_AMOUNT[0]}`,
      loading: false,
      displayCreatedApiKey: false,
    };
  },
  computed: {
    ...mapGetters(['createdApiKey', 'userPoolId']),
    expirationItems() {
      return EXPIRATION_DAYS_AMOUNT.map(amount => ({ value: `${amount}`, title: this.$t(`add.api.key.popup.expiration.${amount}`) }));
    },
    saveButtonDisabled() {
      return !EXPIRATION_DAYS_AMOUNT.includes(this.numericExpirationValue);
    },
    numericExpirationValue() {
      return parseInt(this.expiration, 10);
    },
  },
  watch: {
    createdApiKey: {
      handler() {
        if (this.createdApiKey?.apiKey?.length) {
          this.loading = false;
          this.displayCreatedApiKey = true;
        }
      },
      deep: true,
    },
  },
  methods: {
    copyValue(id) {
      const r = document.createRange();
      r.selectNode(document.getElementById(id));
      window.getSelection().removeAllRanges();
      window.getSelection().addRange(r);
      document.execCommand('copy');
      window.getSelection().removeAllRanges();
    },
    async onCloseClickButton() {
      this.clear();
      this.$emit('closed');
      await this.$store.dispatch(CLEAR_CREATED_API_KEY);
    },
    async onSaveClick() {
      this.loading = true;
      this.$emit('save', {
        description: this.description,
        expiration: this.numericExpirationValue,
      });
      this.clear();
    },
    clear() {
      this.expiration = `${EXPIRATION_DAYS_AMOUNT[0]}`;
      this.description = '';
      this.displayCreatedApiKey = false;
    },
  },
};
</script>

<style lang='scss' scoped>
@import '~@/assets/styles/design-system';

.add-api-key-popup{
  height: rem(280);
  &.display-created-api-key {
    height: rem(375);
  }
  &__create-api-key {
    &__action {
      .close_btn {
        margin-right: $space-1-5;
        margin-top: calc(#{$space-2} + #{$space-0-5});
        float: right;

        &:hover {
          cursor: pointer;
          color: var(--brand-primary);
        }
      }
    }
    &__header{
      padding-top: rem(35);
      padding-left: rem(35);
      .header-label{
        color: var(--text-primary);
        font-family: $font-family-bold;
        font-size: rem(18);
        font-weight: bold;
        line-height: $font-size-xl;
        letter-spacing: 0;
      }
    }
    &__form{
      margin-left: rem(35);
      margin-top: rem(10);
      .type-select {
        margin-right: rem(10);
        min-width: rem(180);
        width: rem(180);
        .el-input__inner {
          height: $space-8;
        }
      }
      .settings-field {
        margin-bottom: rem(10);
        padding-right: rem(35);
        .text-field{
          margin-top: $space-1-5;
        }
        .label {
          text-transform: uppercase;
          color: var(--text-secondary);
          font-family: $font-family-regular;
          font-size: $font-size-xs;
          letter-spacing: 0.8px;
          line-height: $font-size-xs;
        }
      }
    }
    &__footer {
      padding-bottom: $space-5;
      .buttons {
        text-align: right;
        padding-top: rem(30);
        padding-right: rem(22);

        .cancel-button {
          height: $space-8;
          border-radius: $radius-sm;
        }

        .save-button {
          height: $space-8;
          border-radius: $radius-sm;
          margin-left: $space-2;
          box-shadow: $shadow-sm !important;
        }
      }
    }
  }
  &__display-result {
    &__header{
      padding-top: rem(35);
      padding-left: rem(35);
      .header-label{
        color: var(--text-primary);
        font-family: $font-family-bold;
        font-size: rem(18);
        font-weight: bold;
        line-height: $font-size-xl;
        letter-spacing: 0;
      }
    }
    &__comment {
      padding-left: rem(35);
      .header-comment {
        margin-top: $space-5;
        color: var(--text-primary);
        font-family: $font-family-medium;
        font-size: $font-size-base;
        line-height: $font-size-xl;
        letter-spacing: 0;
      }
    }
    &__body {
      margin-top: rem(10);
      padding: rem(10) rem(35);
      display: flex;
      flex-direction: column;
      &__api-key {
        display: flex;
        align-items: center;
        margin-top: rem(10);
        padding: rem(10) rem(35);
        font-family: $font-family-regular;
        font-size: $font-size-sm;
        &__copy {
          flex: 1;
          align-self: flex-start;
          padding-top: $space-1;
          padding-left: $space-1-5;
          float: right;
          .v-btn--icon{
            color: var(--brand-primary);
          }
          .v-icon {
            height: $font-size-xl;
            font-size: $font-size-xl;
            width: $font-size-xl;
          }
        }
        &__label {
          flex: 55%;
          margin-right: rem(7);
          color: var(--text-secondary);
          font-family: $font-family-medium;
          text-transform: uppercase;
          font-size: $font-size-xs;
          letter-spacing: 1.1px;
          line-height: rem(15);
          align-self: flex-start;
          padding-top: $space-2;
        }
        &__value {
          height: rem(100);
          max-width: rem(550);
          padding: $space-2;
          font-family: $font-family-regular;
          font-size: $font-size-sm;
          overflow-wrap: break-word;
          overflow: auto;
        }
      }
      &__user-pool-id {
        display: flex;
        align-items: center;
        margin-top: rem(10);
        padding: rem(10) rem(35);
        font-family: $font-family-regular;
        font-size: $font-size-sm;
        &__label {
          margin-right: rem(7);
          color: var(--text-secondary);
          font-family: $font-family-medium;
          text-transform: uppercase;
          font-size: $font-size-xs;
          letter-spacing: 1.1px;
          line-height: rem(15);
          padding-top: $space-0-5;
        }
        &__value {
          max-width: rem(550);
          padding: $space-2;
          overflow-wrap: break-word;
          overflow: auto;
        }
        &__copy {
          flex: 1;
          align-self: end;
          padding-left: $space-1-5;
          float: right;
          .v-btn--icon{
            color: var(--brand-primary);
          }
          .v-icon {
            height: $font-size-xl;
            font-size: $font-size-xl;
            width: $font-size-xl;
          }
        }
      }
    }
    &__footer {
      padding-bottom: $space-5;
      .buttons {
        text-align: right;
        padding-top: $space-5;
        padding-right: rem(22);
        .ok-button {
          height: $space-8;
          border-radius: $radius-sm;
          margin-left: $space-2;
        }
      }
    }
  }
}
</style>
<style lang='scss'>
@import '~@/assets/styles/design-system';

.add-api-key-popup {
  .type-select span {
    text-transform: uppercase;
  }
}
</style>
